import { AuthContextType, User } from '@alert-manager/lib';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { authAPI } from '../api/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session in localStorage
    const checkStoredAuth = async () => {
      const storedUser = localStorage.getItem('alertmanager_user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser.token) {
            // Validate token with backend
            const isValidToken = await authAPI.validateToken(parsedUser.token);
            if (isValidToken) {
              setUser(parsedUser);
            } else {
              localStorage.removeItem('alertmanager_user');
            }
          } else {
            // Old session without token, remove it
            localStorage.removeItem('alertmanager_user');
          }
        } catch {
          localStorage.removeItem('alertmanager_user');
        }
      }
      setIsLoading(false);
    };

    checkStoredAuth();
  }, []);

  const login = async (username: string, password: string, rememberMe?: boolean): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authAPI.login({ username, password, rememberMe });

      if (response.success && response.token && response.user) {
        const newUser: User = {
          username: response.user.username,
          role: response.user.role,
          loginTime: new Date().toISOString(),
          token: response.token,
        };
        setUser(newUser);
        localStorage.setItem('alertmanager_user', JSON.stringify(newUser));
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch {
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const register = async (username: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await authAPI.register({ username, password });

      if (response.success && response.token && response.user) {
        const newUser: User = {
          username: response.user.username,
          role: response.user.role,
          loginTime: new Date().toISOString(),
          token: response.token,
        };
        setUser(newUser);
        localStorage.setItem('alertmanager_user', JSON.stringify(newUser));
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch {
      return {
        success: false,
        message: 'Registration failed. Please try again.',
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('alertmanager_user');
  };

  const isAuthenticated = !!user && !!user.token;

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    isAuthenticated,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
